/**
 * "Mark as paid" confirmation (spec §8.4).
 *
 * The preview is fetched from the server rather than computed here, so what the
 * operator approves is literally what the renewal engine will write — a client-side
 * re-implementation of the period maths would eventually disagree with it.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { ArrowRight, CalendarClock, Info, TriangleAlert } from 'lucide-react';
import {
  formatPolicyRef,
  normaliseAmountInput,
  resolveCommissionRate,
  today,
  type PolicyDto,
} from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Skeleton } from '@/components/ui/misc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEnumOptions } from '@/components/common/enum-badge';
import { useInsuranceCompanies, useMarkPolicyPaid, useRenewalPreview } from '@/lib/api/domains';
import { useOffline } from '@/lib/offline/context';
import { useI18n } from '@/lib/i18n';
import { ApiRequestError } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { randomUUID } from '@/lib/uuid';

/**
 * The row shared by the current-period and next-period cards.
 *
 * It is a named constant rather than two copies of a class string because the whole point
 * is that both cards resolve to the SAME axes — this dialog asks the operator to compare
 * two premiums and two commissions, and a figure can only be compared against one above
 * it if the two sit on one vertical line.
 *
 * They did not. Both rows were `flex … justify-between`, which pins the money block to the
 * right edge and lets its *start* float according to its own width. The current period
 * spelled its commission out («Προμήθεια 98,65 €») and the next period did not
 * («98,65 €»), so the two premium figures — identical values, stacked 14px apart — landed
 * 76px off from each other. A fixed second track fixes it by construction: 9rem holds
 * «Προμήθεια 1.234,56 €» at 12px with room to spare, and neither card can drift because
 * neither card chooses the width.
 *
 * The premium and its commission stack inside that track instead of running inline, which
 * is what buys the row vertical air without widening the dialog.
 */
const PERIOD_ROW = 'grid grid-cols-[minmax(0,1fr)_9rem] items-baseline gap-x-4';

/**
 * What the operator may change about the period being opened. Every one of these is something an
 * insurer actually varies at renewal: a reprice, a rate change, or a term that does not follow
 * the frequency because the carrier realigned an anniversary or issued short cover.
 */
interface NextPeriodDraft {
  renewalFrequency: string;
  startDate: string;
  endDate: string;
  premiumAmount: string;
  netPremiumAmount: string;
  commissionPercentage: string;
}

/**
 * Holds a value still, briefly, so a preview request follows a pause in typing rather than every
 * keystroke. 400ms: long enough that «1.234,56» is one request, short enough that the commission
 * appears while the operator is still looking at the field that changed it.
 */
function useSettled<T>(value: T, delay = 400): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/** Label, control and optional hint — the dialog's own, so the grid rows line up. */
function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-[11.5px]">
        {label}
      </Label>
      {children}
      {hint && <p className="text-muted-soft text-[11px] leading-snug">{hint}</p>}
    </div>
  );
}

export function MarkPaidDialog({
  policy,
  open,
  onOpenChange,
}: {
  policy: PolicyDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, money, date } = useI18n();
  const methodOptions = useEnumOptions('paymentMethod');
  // A renewal opens a period, so «Εφάπαξ» is not on offer — a one-off has no next period.
  const frequencyOptions = useEnumOptions('frequency').filter((option) => option.value !== 'one_off');

  /**
   * The carrier's rate for this policy's vehicle category, as it stands today.
   *
   * The server proposes the closing period's percentage, which is the right answer only for a
   * straight re-issue. Rates are agreed per carrier and per category and move between years —
   * and the archive import left most of the book at 0,00 %, so every renewal of an imported
   * policy offered a commission of nothing and had to be typed in by hand. A period opening
   * today earns today's tariff, so that is what fills the field.
   */
  const { data: insurers } = useInsuranceCompanies();
  const tariffRate = React.useMemo(
    () =>
      resolveCommissionRate(
        insurers?.find((insurer) => insurer.id === policy.insuranceCompanyId),
        policy.vehicleCategory,
      ),
    [insurers, policy.insuranceCompanyId, policy.vehicleCategory],
  );

  /**
   * «Ειδική προμήθεια» — off by default, and the percentage is read-only while it is.
   *
   * Almost every renewal is on the carrier's tariff, and a rate that can be typed by accident
   * is a rate that will be: the field sits between two money inputs and a stray keystroke there
   * books the wrong commission for the whole period. Ticking the box is the operator saying this
   * one is on a negotiated rate — the field then holds what they type and stops following the
   * tariff. Shown only where there IS a tariff to depart from; a carrier with no rate on file
   * leaves the field open, exactly as it was.
   */
  const [specialCommission, setSpecialCommission] = React.useState(false);
  const commissionLocked = tariffRate !== null && !specialCommission;

  const [paymentMethod, setPaymentMethod] = React.useState('bank_transfer');
  const [paymentDate, setPaymentDate] = React.useState(() => today());

  /**
   * The ανανεωτήριο number for the period about to open.
   *
   * Captured here rather than left to a later edit because this is the one moment the agent
   * is holding the certificate: the insurer sends it with the renewal notice, and a number
   * that is not typed now is a number nobody goes back for. Starts empty, never pre-filled
   * from the closing period — that number belongs to the certificate being retired.
   */
  const [nextRenewalNumber, setNextRenewalNumber] = React.useState('');

  /**
   * The operator's edits to the period being opened, and which of them are actually edits.
   *
   * `draft` is seeded from the first preview and is what the inputs display. `touched` is what
   * gets sent: an untouched field must stay «as the server computes it», not be echoed back as
   * an override, or every renewal would pin next year's dates to the figures this dialog
   * happened to render — including for the operator who changed nothing.
   */
  const [draft, setDraft] = React.useState<NextPeriodDraft | null>(null);
  const [touched, setTouched] = React.useState<Set<keyof NextPeriodDraft>>(new Set());

  const { isOnline } = useOffline();

  /**
   * The edited fields, as they stand right now.
   *
   * Split from the debounced copy below because the two are wanted for different things, and
   * conflating them lost data: `submit` sent the *settled* overrides, so typing a premium and
   * pressing «Επιβεβαίωση» inside the same 400ms wrote the previous figure and said nothing.
   * What the operator can see in the fields is what gets sent.
   */
  const edits = React.useMemo(() => {
    if (!draft) return {};
    const sent: Record<string, string> = {};
    if (touched.has('renewalFrequency')) sent.nextRenewalFrequency = draft.renewalFrequency;
    if (touched.has('startDate')) sent.nextStartDate = draft.startDate;
    if (touched.has('endDate')) sent.nextEndDate = draft.endDate;
    /*
     * Normalised on the way out, never in the field: «59,5» is what the office types and what
     * the app renders back, «59.5» is what the parser accepts, and rewriting the box mid-word
     * would fight the caret. A value still being typed («59,») resolves to undefined and is
     * simply not sent — the server then prices the period on the stored figure, which is the
     * same thing an untouched field means.
     */
    const premium = normaliseAmountInput(draft.premiumAmount);
    const netPremium = normaliseAmountInput(draft.netPremiumAmount);
    const commissionPct = normaliseAmountInput(draft.commissionPercentage);
    if (touched.has('premiumAmount') && premium) sent.nextPremiumAmount = premium;
    if (touched.has('netPremiumAmount') && netPremium) sent.nextNetPremiumAmount = netPremium;
    if (touched.has('commissionPercentage') && commissionPct) sent.nextCommissionPercentage = commissionPct;
    return sent;
  }, [draft, touched]);

  /** Which touched amounts are not a number yet — the ones that must not reach a write. */
  const unfinished = React.useMemo(
    () =>
      (['premiumAmount', 'netPremiumAmount', 'commissionPercentage'] as const).filter(
        (field) => touched.has(field) && normaliseAmountInput(draft?.[field] ?? '') === undefined,
      ),
    [draft, touched],
  );

  // The preview, and only the preview, waits for typing to settle — one request per figure.
  const overrides = useSettled(edits);

  // Renewal is the one action that must never be queued: it archives a period and
  // recomputes dates from server time, so a delayed copy of this decision would write
  // the wrong period. Blocked outright rather than optimistically accepted.
  const preview = useRenewalPreview(policy.id, open && isOnline, overrides);

  /**
   * The last preview that actually arrived.
   *
   * A failed request leaves `data` undefined, and the body rendered `null` for that — so one
   * rejected figure blanked the current period, the next period and every input with it. The
   * figures on screen are worth more than the blank: they are what the operator was reading,
   * they are labelled as not-recalculated below, and nothing is written from them without the
   * server pricing the period again.
   */
  const lastGood = React.useRef<NonNullable<typeof preview.data> | null>(null);
  React.useEffect(() => {
    if (preview.data) lastGood.current = preview.data;
  }, [preview.data]);
  const markPaid = useMarkPolicyPaid();

  /**
   * One idempotency key per *opening* of this dialog — that is the unit of intent.
   * Regenerated when it reopens, so collecting the next period is a new decision, but
   * stable across a double-click, an impatient second click while the request is in
   * flight, or a retry after a dropped response. Without it each of those archives
   * another period and books the commission again.
   */
  const [clientRef, setClientRef] = React.useState(() => randomUUID());
  React.useEffect(() => {
    if (open) setClientRef(randomUUID());
  }, [open]);

  /*
   * Reopening is a fresh decision, so last time's edits do not survive it — the same reason the
   * idempotency key is reminted above.
   */
  React.useEffect(() => {
    if (!open) {
      setDraft(null);
      setTouched(new Set());
      setSpecialCommission(false);
      lastGood.current = null;
    }
  }, [open]);

  /*
   * Seeded from the first preview to arrive and not from later ones: after that the inputs hold
   * the operator's typing, and re-seeding from a response their own edit provoked would fight
   * the cursor.
   */
  const seeded = React.useRef(false);
  /*
   * A frequency change re-derives the end date — τρίμηνο → μηνιαίο must turn 20/01 into 20/11,
   * not leave the quarterly date under a monthly label. The one exception to «seed once»: it
   * takes the end date from the first preview priced on the NEW frequency, and only while the
   * operator has not typed an end date of their own after choosing it.
   */
  const reseedEnd = React.useRef(false);
  React.useEffect(() => {
    if (!open) { seeded.current = false; reseedEnd.current = false; return; }
    const next = preview.data?.nextPeriod;
    if (!next) return;
    if (seeded.current) {
      if (reseedEnd.current && next.renewalFrequency === draft?.renewalFrequency) {
        reseedEnd.current = false;
        setDraft((current) => (current ? { ...current, endDate: next.endDate } : current));
      }
      return;
    }
    seeded.current = true;
    setDraft({
      renewalFrequency: next.renewalFrequency,
      startDate: next.startDate,
      endDate: next.endDate,
      premiumAmount: next.premiumAmount,
      netPremiumAmount: next.netPremiumAmount ?? '',
      commissionPercentage: next.commissionPercentage,
    });
  }, [open, preview.data, draft?.renewalFrequency]);

  /*
   * Applied after the seed rather than inside it, because the carrier list and the preview are
   * two requests and either can land first. It re-runs when «Ειδική προμήθεια» is unticked, so
   * abandoning an override snaps the field back to the tariff instead of stranding the typed
   * value. Guarded on equality, so it settles after one write.
   */
  React.useEffect(() => {
    if (!open || specialCommission || tariffRate === null) return;
    if (!draft || draft.commissionPercentage === tariffRate) return;
    setDraft((current) => (current ? { ...current, commissionPercentage: tariffRate } : current));
    setTouched((current) => new Set(current).add('commissionPercentage'));
  }, [open, specialCommission, tariffRate, draft]);

  const edit = (field: keyof NextPeriodDraft) => (value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
    setTouched((current) => new Set(current).add(field));
    if (field === 'endDate') reseedEnd.current = false;
  };

  /*
   * Choosing a frequency hands the end date back to the server: a hand-typed end belonged to the
   * old term, and keeping it would send «monthly» with a quarterly period attached.
   */
  const changeFrequency = (value: string) => {
    edit('renewalFrequency')(value);
    reseedEnd.current = true;
    setTouched((current) => {
      const nextTouched = new Set(current);
      nextTouched.delete('endDate');
      return nextTouched;
    });
  };

  /*
   * Set synchronously, unlike `isPending`, which only disables the button after React has
   * re-rendered — two clicks inside that gap each sent a request (IOH4401, 22/09/2026).
   */
  const submitting = React.useRef(false);

  const submit = async () => {
    if (submitting.current) return;
    // Refusing beats guessing: dropping the field would write the stored figure, and coercing
    // «59,» to 59 would invent a premium the operator never finished typing.
    if (unfinished.length > 0) {
      toast.error(t('policy.amountIncomplete'));
      return;
    }
    submitting.current = true;
    try {
      const result = await markPaid.mutateAsync({
        policyId: policy.id,
        paymentMethod,
        paymentDate,
        // Trimmed to null, not sent as '': an empty string would be stored as an empty
        // certificate number, which sorts and searches as a value rather than as absence.
        nextRenewalNumber: nextRenewalNumber.trim() || null,
        // The fields as they stand on screen, not the debounced copy the preview last ran on.
        // Spread rather than listed, because anything absent here means «as the server
        // computes it» — which is exactly what an untouched field should mean.
        ...edits,
        clientRef,
        expectedPeriodNumber: policy.currentPeriodNumber,
      });

      toast.success(
        result.renewed
          ? `${formatPolicyRef(policy.policyNumber, policy.plates)} · ${t('policy.nextPeriod')} ${date(result.policy.startDate)} – ${date(result.policy.endDate)}`
          : (result.reason ?? t('common.saved')),
      );
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : t('error.generic'));
    } finally {
      submitting.current = false;
    }
  };

  const data = preview.data ?? lastGood.current ?? undefined;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(policy.paymentStatus === 'paid' ? 'policy.renewTitle' : 'policy.markPaidTitle')}
          </DialogTitle>
          <DialogDescription>
            {formatPolicyRef(policy.policyNumber, policy.plates)} · {policy.clientName}
            {policy.insuranceCompanyName && <> · {policy.insuranceCompanyName}</>}
          </DialogDescription>
        </DialogHeader>

        {/* space-y-5 rather than -4: the payment fields are a separate act from reading the
            preview, and at 16px the first Label sat as close to the preview card as the
            card's own contents sat to each other. */}
        <DialogBody className="space-y-5">
          {!isOnline ? (
            <div className="border-serious/35 bg-serious/[0.08] flex items-start gap-2 rounded-lg border p-3">
              <TriangleAlert className="text-serious mt-0.5 size-4 shrink-0" />
              <div>
                <p className="text-sm font-medium">{t('offline.requiresConnection')}</p>
                <p className="text-muted-foreground mt-0.5 text-[13px]">
                  {t('offline.blocked.financial')} — {t('offline.offlineHint')}
                </p>
              </div>
            </div>
          ) : preview.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : data ? (
            <div className="space-y-3">
              {/*
                Said plainly rather than shown by blanking the card. The operator is looking at
                real figures that simply are not the ones their last keystroke asked for, and
                the only wrong move here is to let them believe otherwise.
              */}
              {preview.isError && (
                <p className="text-serious flex items-center gap-1.5 text-[11.5px]">
                  <TriangleAlert className="size-3.5 shrink-0" />
                  {t('policy.previewStale')}
                </p>
              )}
              <div className="bg-muted/50 rounded-lg border p-4">
                <p className="text-muted-foreground mb-2.5 text-xs font-medium">
                  {t('policy.currentPeriod')}
                </p>
                <div className={PERIOD_ROW}>
                  <span className="text-sm tabular">
                    {date(data.archivedPeriod.startDate)} – {date(data.archivedPeriod.endDate)}
                  </span>
                  <span className="text-right">
                    <span className="block text-sm tabular">
                      {money(data.archivedPeriod.premiumAmount)}
                    </span>
                    <span className="text-muted-foreground mt-1 block text-xs tabular">
                      {t('policy.commission')} {money(data.archivedPeriod.commissionAmount)}
                    </span>
                  </span>
                </div>
              </div>

              {data.willRenew && data.nextPeriod ? (
                <>
                  <div className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs">
                    <ArrowRight className="size-3.5 rotate-90" />
                  </div>

                  {/*
                    Editable, not a summary. What the insurer issues at renewal routinely differs
                    from what the frequency implies — a reprice, a new rate, a term realigned to a
                    fleet anniversary — and the operator is holding the certificate that says so.
                    Every field is pre-filled with the computed value, so the common case is still
                    «read it, confirm it» and nothing has to be retyped to accept it.
                  */}
                  <div className="border-primary/30 bg-primary/[0.06] space-y-3 rounded-lg border p-4">
                    <p className="text-primary flex items-center gap-1.5 text-xs font-medium">
                      <CalendarClock className="size-3.5" />
                      {t('policy.nextPeriod')}
                    </p>

                    <div className="grid gap-3 sm:grid-cols-2">
                      {/* Spans both columns: it decides the end date below, so it reads first. */}
                      <div className="sm:col-span-2">
                        <Field label={t('policy.frequency')} htmlFor="nextRenewalFrequency">
                          <Select value={draft?.renewalFrequency ?? ''} onValueChange={changeFrequency}>
                            <SelectTrigger id="nextRenewalFrequency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencyOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                      <Field label={t('policy.startDate')} htmlFor="nextStartDate">
                        <Input
                          id="nextStartDate"
                          type="date"
                          value={draft?.startDate ?? ''}
                          onChange={(event) => edit('startDate')(event.target.value)}
                        />
                      </Field>
                      <Field label={t('policy.endDate')} htmlFor="nextEndDate">
                        <Input
                          id="nextEndDate"
                          type="date"
                          value={draft?.endDate ?? ''}
                          onChange={(event) => edit('endDate')(event.target.value)}
                        />
                      </Field>
                      <Field label={t('policy.premium')} htmlFor="nextPremium">
                        <Input
                          id="nextPremium"
                          inputMode="decimal"
                          className="tabular"
                          value={draft?.premiumAmount ?? ''}
                          onChange={(event) => edit('premiumAmount')(event.target.value)}
                        />
                      </Field>
                      <Field
                        label={t('policy.netPremium')}
                        htmlFor="nextNetPremium"
                        hint={t('policy.netPremiumHint')}
                      >
                        <Input
                          id="nextNetPremium"
                          inputMode="decimal"
                          className="tabular"
                          value={draft?.netPremiumAmount ?? ''}
                          placeholder={t('policy.netPremiumMissing')}
                          onChange={(event) => edit('netPremiumAmount')(event.target.value)}
                        />
                      </Field>
                      <Field label={t('policy.commissionPct')} htmlFor="nextCommissionPct">
                        <Input
                          id="nextCommissionPct"
                          inputMode="decimal"
                          className="tabular"
                          value={draft?.commissionPercentage ?? ''}
                          disabled={commissionLocked}
                          onChange={(event) => edit('commissionPercentage')(event.target.value)}
                        />
                        {tariffRate !== null && (
                          <label className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                            <Checkbox
                              checked={specialCommission}
                              onCheckedChange={(checked) => setSpecialCommission(checked === true)}
                              className="size-3.5"
                            />
                            {t('policy.specialCommission')}
                          </label>
                        )}
                      </Field>
                      {/*
                        The one figure that is NOT an input: it is computed by the same engine
                        that will book it, on the καθαρά, and echoed back with every edit. A
                        commission the operator could type would be a commission that disagrees
                        with the ledger.
                      */}
                      <div className="flex flex-col justify-end">
                        <span className="text-muted-soft text-[11.5px] font-medium">
                          {t('policy.commission')}
                        </span>
                        <span
                          className={cn(
                            'mt-1.5 text-[15px] leading-none font-semibold tabular transition-opacity duration-150',
                            preview.isFetching && 'opacity-40',
                          )}
                        >
                          {money(data.nextPeriod.commissionAmount)}
                        </span>
                      </div>
                    </div>

                    <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                      <Info className="size-3.5 shrink-0" />
                      {t('policy.nextRenewal')}: {date(data.nextPeriod.nextRenewalDate)}
                    </p>
                  </div>
                </>
              ) : (
                <div className="border-serious/35 bg-serious/[0.08] flex items-start gap-2 rounded-lg border p-3">
                  <TriangleAlert className="text-serious mt-0.5 size-4 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{t('policy.willNotRenew')}</p>
                    <p className="text-muted-foreground mt-0.5 text-[13px]">{data.reason}</p>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className={cn('grid gap-4 sm:grid-cols-2', !isOnline && 'hidden')}>
            <div className="space-y-1.5">
              <Label htmlFor="paymentDate">{t('policy.dueDate')}</Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paymentMethod">{t('policy.paymentMethod')}</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="paymentMethod">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {methodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Spans both columns: it is a longer value than a date or a method, and the hint
                needs room to say that blank is a legitimate answer. */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="nextRenewalNumber">{t('policy.nextRenewalNumber')}</Label>
              <Input
                id="nextRenewalNumber"
                value={nextRenewalNumber}
                onChange={(event) => setNextRenewalNumber(event.target.value)}
                placeholder={policy.renewalNumber ?? undefined}
              />
              <p className="text-muted-foreground text-xs">{t('policy.nextRenewalNumberHint')}</p>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={markPaid.isPending} disabled={!isOnline}>
            {t('action.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
